// apps/client/src/characters/species.ts — villager species parts on the chibi body (characters pass, 2026-10-16): the ears, snouts, bills,
// tails and fur patterns that make a cat, dog, rabbit, bear, duck, frog, sheep or squirrel read at a glance, mostly from above (the town
// camera). Parts are named `ears:<kind>`, `tail:<kind>`, `bill`, `muzzle`, `eyes:frog`, `wool:head|body` and `pattern:<pattern>`.
import type { FurPattern, Species } from '@whiskerton/content';
import * as THREE from 'three';
import { EYE_LAT, EYE_LON, face, HEAD, onHead, smile, stick } from './head';
import { group, part, shade } from './part';
import { blob, locks, shell, taperedTube, teardrop } from './shapes';

export interface AnimalSpec {
  kind: Exclude<Species, 'person'>; fur: string; ears: string; muzzle: string; eyes?: string;
  /** The sheep's wool (its fur colour; its face is `fur`). */
  wool?: string;
  pattern: FurPattern; patternColor: string;
}
const NOSE_DARK = '#3B2A20', NOSE_PINK = '#E58A9A', INNER = '#F4B6A8', INNER_RABBIT = '#F4B6C4', TOOTH = '#FFFDF6';
const Z = new THREE.Vector3(0, 0, 1);
const v = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);

/** One ear on the head at (lat, lon): a drop standing out of the surface, tipped `out` away from the middle and `back` toward the nape,
 *  flattened front to back by `flat`, with an optional inner ear. Both ears go in one named group. */
function ear(g: THREE.Object3D, len: number, r: number, hex: string, lat: number, lon: number, out: number, back: number, flat: number, inner?: string,
  o: { peak?: number; tuft?: string } = {}): void {
  const { p, n } = onHead(lat, lon, -0.012);
  const e = new THREE.Group();
  e.position.copy(p);
  // Stand the ear along the surface normal, then lean it: out from the head's middle (about its own forward axis) and back.
  e.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), n);
  e.rotateZ(-Math.sign(lon) * out);
  e.rotateX(-back);
  g.add(e);
  part(e, teardrop(len, r, 8, 6, o.peak), hex).scale.z = flat;
  if (inner) {
    const i = part(e, teardrop(len * 0.74, r * 0.6, 6, 5, o.peak), inner, [0, len * 0.1, r * flat * 0.62]);
    i.scale.z = flat * 0.45;
  }
  if (o.tuft) part(e, teardrop(len * 0.6, r * 0.45, 6, 4), o.tuft, [0, len * 0.8, 0]).scale.z = flat;   // a tuft at the tip
}

export function dressAnimal(head: THREE.Object3D, torso: THREE.Object3D, s: AnimalSpec): void {
  const { kind } = s;
  group(head, `species:${kind}`);
  const ears = group(head, `ears:${kind}`);
  const snout = (rx: number, ry: number, rz: number, lat: number, lift: number) =>
    stick(head, blob(rx, ry, rz, 12, 8), s.muzzle, lat, 0, lift, 'muzzle');
  const noseAt = (hex: string, rx: number, lat: number, lift: number) => stick(head, blob(rx, rx * 0.72, rx * 0.75, 10, 6), hex, lat, 0, lift, 'nose');
  const eyeLat = EYE_LAT - 0.02, eyeLon = EYE_LON + 0.04;

  switch (kind) {
    case 'cat': {
      face(head, { eyes: s.eyes, skin: s.fur, eyeLat, eyeLon, mouthLat: null });
      for (const sx of [-1, 1]) ear(ears, 0.11, 0.06, s.ears, 0.95, sx * 0.6, 0.3, 0.1, 0.5, INNER, { peak: 0.12 });
      for (const sx of [-1, 1]) stick(head, blob(0.036, 0.03, 0.026, 10, 6), s.muzzle, eyeLat - 0.32, sx * 0.13, -0.006);   // the two-lobed muzzle
      noseAt(NOSE_PINK, 0.017, eyeLat - 0.22, 0.012);
      for (const sx of [-1, 1]) smile(head, eyeLat - 0.36, sx * 0.07, { r: 0.012, lift: 0.016 });                       // a little "w"
      const tail = part(torso, taperedTube([v(0, 0.04, -0.09), v(0, 0.02, -0.17), v(0.02, 0.1, -0.24), v(0.05, 0.2, -0.22), v(0.06, 0.24, -0.16)],
        (t) => 0.024 - 0.008 * t, 16, 6), s.fur, [0, 0, 0], 'tail:cat');
      part(tail, blob(0.02, 0.02, 0.02, 8, 6), s.ears, [0.06, 0.24, -0.16]);
      break;
    }
    case 'dog': {
      face(head, { eyes: s.eyes, skin: s.fur, eyeLat, eyeLon, mouthLat: null });
      for (const sx of [-1, 1]) {                                                   // floppy ears hanging down the sides of the head
        const { p, n } = onHead(0.72, sx * 1.02, -0.004);
        const e = part(ears, teardrop(0.14, 0.058, 8, 6), s.ears, [p.x, p.y, p.z]);
        e.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), n.clone().multiplyScalar(0.3).add(v(sx * 0.3, -1, 0.06)).normalize());   // tip down
        e.scale.z = 0.4;
      }
      snout(0.074, 0.054, 0.058, eyeLat - 0.34, -0.02);
      noseAt(NOSE_DARK, 0.026, eyeLat - 0.2, 0.034);
      smile(head, eyeLat - 0.42, 0, { name: 'mouth', lift: 0.03 });
      const tail = taperedTube([v(0, 0.06, -0.1), v(0, 0.1, -0.15), v(0, 0.17, -0.16)], (t) => 0.024 - 0.01 * t, 8, 6);
      part(torso, tail, s.fur, [0, 0, 0], 'tail:dog');
      break;
    }
    case 'rabbit': {
      face(head, { eyes: s.eyes, skin: s.fur, eyeLat, eyeLon, mouthLat: null });
      ear(ears, 0.18, 0.045, s.ears, 1.18, 0.3, 0.16, 0.1, 0.45, INNER_RABBIT);     // tall, but under the name tag (nametags HEAD_TAG_HEIGHT)
      ear(ears, 0.17, 0.045, s.ears, 1.18, -0.3, 0.36, 0.06, 0.45, INNER_RABBIT);    // one ear leans out: a bit of character
      snout(0.05, 0.036, 0.04, eyeLat - 0.32, -0.014);
      noseAt(NOSE_PINK, 0.015, eyeLat - 0.24, 0.022);
      stick(head, blob(0.013, 0.014, 0.005, 6, 4), TOOTH, eyeLat - 0.42, 0, 0.022);  // buck teeth
      part(torso, blob(0.048, 0.046, 0.044, 10, 8), s.muzzle, [0, 0.04, -0.115], 'tail:rabbit');
      break;
    }
    case 'bear': {
      face(head, { eyes: s.eyes, skin: s.fur, eyeLat, eyeLon, mouthLat: null });
      for (const sx of [-1, 1]) {
        const { p, n } = onHead(0.98, sx * 0.72, 0.012);
        const e = part(ears, blob(0.05, 0.048, 0.026, 12, 8), s.ears, [p.x, p.y, p.z]);
        e.quaternion.setFromUnitVectors(Z, n.clone().lerp(v(0, 0, 1), 0.75).normalize());   // facing forward, so from above they are ears
        part(e, blob(0.026, 0.024, 0.01, 8, 6), shade(s.ears, 0.82), [0, -0.004, 0.02]);
      }
      snout(0.07, 0.05, 0.056, eyeLat - 0.34, -0.018);
      noseAt(NOSE_DARK, 0.024, eyeLat - 0.22, 0.03);
      smile(head, eyeLat - 0.42, 0, { r: 0.014, name: 'mouth', lift: 0.028 });
      part(torso, blob(0.036, 0.034, 0.03, 8, 6), s.fur, [0, 0.04, -0.112], 'tail:bear');
      break;
    }
    case 'duck': {
      face(head, { eyes: s.eyes, skin: s.fur, eyeLat: eyeLat + 0.02, eyeLon, mouthLat: null });
      const bill = stick(head, blob(0.068, 0.024, 0.062, 12, 6), s.muzzle, eyeLat - 0.3, 0, 0.03, 'bill');
      bill.rotateX(0.12);
      part(bill, blob(0.06, 0.008, 0.05, 10, 4), shade(s.muzzle, 0.82), [0, -0.012, -0.004]);   // the lower bill
      for (const [i, a] of [-0.3, 0, 0.3].entries()) {                                         // a tuft on top
        const { p, n } = onHead(1.36 - (i === 1 ? 0 : 0.06), a, -0.01);
        const t = part(ears, teardrop(i === 1 ? 0.09 : 0.065, 0.02, 6, 4), s.ears, [p.x, p.y, p.z]);
        t.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), n.clone().add(v(0, 0, -0.5)).normalize());
      }
      const tail = part(torso, teardrop(0.09, 0.05, 8, 5), s.fur, [0, 0.06, -0.1], 'tail:duck');
      tail.rotation.x = -2.1;
      tail.scale.x = 1.3;
      break;
    }
    case 'frog': {
      const bumps = group(head, 'eyes:frog');
      for (const [side, sx] of [['left', 1], ['right', -1]] as const) {           // big eyes on bumps on top of the head
        const { p } = onHead(0.86, sx * 0.44, -0.01);
        part(bumps, blob(0.058, 0.052, 0.054, 12, 8), s.fur, [p.x, p.y + 0.012, p.z]);
        const look = v(0, Math.sin(0.55), Math.cos(0.55));                            // the pupils look out and up, toward the camera
        const at = v(p.x, p.y + 0.012, p.z).addScaledVector(look, 0.05);
        const white = part(bumps, blob(0.036, 0.04, 0.012, 10, 6), '#FFFDF6', [at.x, at.y, at.z]);
        white.quaternion.setFromUnitVectors(Z, look);
        const pupilAt = at.clone().addScaledVector(look, 0.008);
        const pupil = part(bumps, blob(0.022, 0.028, 0.01, 10, 6), '#2B2118', [pupilAt.x, pupilAt.y, pupilAt.z], `eye-${side}`);
        pupil.quaternion.setFromUnitVectors(Z, look);
        const g = at.clone().addScaledVector(look, 0.016).add(v(0.008, 0.01, 0));
        part(bumps, blob(0.007, 0.007, 0.004, 6, 4), '#FFFFFF', [g.x, g.y, g.z]);
      }
      for (const [side, sx] of [['left', 1], ['right', -1]] as const)
        stick(head, blob(0.027, 0.016, 0.006, 10, 6), '#F3A9A0', -0.1, sx * 0.62, -0.001, `blush-${side}`);
      smile(head, -0.16, 0, { r: 0.07, name: 'mouth', lift: 0.003 }).scale.set(1, 0.45, 1);   // a wide frog grin
      part(torso, blob(0.03, 0.03, 0.03, 6, 4), s.fur, [0, 0.04, -0.105], 'tail:frog');
      break;
    }
    case 'sheep': {
      face(head, { eyes: s.eyes, skin: s.fur, eyeLat, eyeLon, mouthLat: null });
      const wool = s.wool ?? '#FFFBF1';
      part(head, shell({ radii: v(HEAD.r.x + 0.03, HEAD.r.y + 0.03, HEAD.r.z + 0.03), rings: 6, step: (az) => (Math.abs(az) < 1.4 ? 0.15 : 0.22), lip: 0.12,
        edge: (az) => 0.92 + 0.55 * (1 - Math.cos(az)) / 2 + 0.2 * locks(az, 11) ** 0.5,
        puff: (az, polar) => 1 + 0.08 * Math.max(0, Math.cos(az * 6 + polar * 2)) * Math.max(0, Math.cos(polar * 7)) + 0.06 * Math.sin(Math.min(polar, 1.5) * 2) ** 2,
        tone: (az, polar) => 0.93 + 0.1 * Math.max(0, Math.cos(az * 6 + polar * 2)) * Math.max(0, Math.cos(polar * 7)) }), wool, [HEAD.c.x, HEAD.c.y, HEAD.c.z], 'wool:head');
      for (const sx of [-1, 1]) {                                                 // small ears sticking out sideways
        const { p } = onHead(0.1, sx * 1.42, -0.01);
        const e = part(ears, teardrop(0.085, 0.032, 6, 5), s.ears, [p.x, p.y, p.z]);
        e.rotation.set(0, 0, -sx * 1.9);
        e.scale.z = 0.5;
      }
      snout(0.058, 0.04, 0.045, eyeLat - 0.33, -0.012);
      noseAt(NOSE_DARK, 0.016, eyeLat - 0.26, 0.022);
      smile(head, eyeLat - 0.4, 0, { r: 0.012, name: 'mouth', lift: 0.02 });
      const body = group(torso, 'wool:body');                                    // a fluffy wool collar over the shoulders
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        part(body, blob(0.044, 0.037, 0.042, 7, 4), wool, [Math.sin(a) * 0.072, 0.15 + (i % 2) * 0.01, Math.cos(a) * 0.068 - 0.005]);
      }
      part(torso, blob(0.04, 0.04, 0.04, 8, 6), wool, [0, 0.05, -0.11], 'tail:sheep');
      break;
    }
    case 'squirrel': {
      face(head, { eyes: s.eyes, skin: s.fur, eyeLat, eyeLon, mouthLat: null });
      for (const sx of [-1, 1]) {
        ear(ears, 0.085, 0.036, s.ears, 1.0, sx * 0.55, 0.2, 0.1, 0.55, INNER, { peak: 0.2, tuft: shade(s.ears, 0.8) });
        stick(head, blob(0.04, 0.034, 0.028, 10, 6), s.muzzle, eyeLat - 0.32, sx * 0.17, -0.01);   // puffed cheeks
      }
      noseAt(NOSE_DARK, 0.014, eyeLat - 0.24, 0.016);
      stick(head, blob(0.012, 0.013, 0.005, 6, 4), TOOTH, eyeLat - 0.36, 0, 0.012);
      const pts = [v(0, 0.03, -0.1), v(0, 0.06, -0.2), v(0, 0.17, -0.27), v(0, 0.3, -0.25), v(0, 0.38, -0.17), v(0, 0.39, -0.1)];
      part(torso, taperedTube(pts, (t) => 0.035 + 0.05 * Math.sin(Math.PI * Math.min(1, t * 1.15)) ** 0.7, 18, 8), s.fur, [0, 0, 0], 'tail:squirrel');
      break;
    }
  }
  addPattern(head, s.pattern, s.patternColor, s.kind);
}

/** Fur markings in the pattern colour, each part named `pattern:<kind>`, on the head (the shirt covers the back): on the crown, where the town
 *  camera sees them, and round the face. A frog's eyes sit on its crown, so its markings go behind them, on the back of the head: between or
 *  under the eye bumps they ran down the face like tear tracks. */
function addPattern(head: THREE.Object3D, pattern: FurPattern, pc: string, kind: AnimalSpec['kind']): void {
  const name = `pattern:${pattern}`;
  const crown = kind === 'frog' ? { lat: 1.1, lon: Math.PI } : { lat: 0.7, lon: 0 };
  switch (pattern) {
    case 'spots':
      for (const [lat, lon, r] of [[crown.lat, crown.lon + 0.5, 0.032], [crown.lat + 0.25, crown.lon - 0.35, 0.026], [0.3, 2.4, 0.03], [0.45, -2.2, 0.024]] as const)
        stick(head, blob(r, r * 0.9, 0.006, 8, 5), pc, lat, lon, 0.001, name);
      break;
    case 'stripes': {
      // Three short stripes on the crown running front to back, the same distance apart at whatever latitude they sit.
      const lat = crown.lat + 0.12, gap = (0.26 * Math.cos(0.82)) / Math.cos(lat);
      for (const k of [-1, 0, 1]) stick(head, blob(0.012, 0.05, 0.006, 6, 6), pc, lat, crown.lon + k * gap, 0.001, name);
      for (const sx of [-1, 1]) for (const d of [0, 0.1]) stick(head, blob(0.03, 0.008, 0.006, 6, 4), pc, -0.02 - d, sx * 1.15, 0.001, name);
      break;
    }
    case 'patch':   // around the doll's left eye (+x; the doll faces +z)
      stick(head, blob(0.05, 0.052, 0.006, 10, 6), pc, EYE_LAT - 0.03, EYE_LON + 0.06, -0.002, name);
      break;
    case 'mask':    // a band across both eyes
      for (const sx of [-1, 1]) stick(head, blob(0.062, 0.04, 0.006, 10, 6), pc, EYE_LAT - 0.03, sx * (EYE_LON + 0.05), -0.002, name);
      break;
    case 'none':
      break;
  }
}
